/**
 * PortalBuyers — Full-featured buyer marketplace management
 *
 * CRUD with: webhook, Ringba, postback URL, S2S, ping/post,
 * volume caps, state/age/credit targeting, schedule, field mapping.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Store, Plus, Loader2, DollarSign, Phone, Globe, Pause, Play,
  Pencil, Save, ArrowLeft, X, Clock, MapPin, Shield, Settings, Tag,
} from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch } from '@/utils/supabaseRest';
import { Link } from 'react-router-dom';

interface Buyer {
  id: string;
  name: string;
  company: string | null;
  contact_email: string;
  phone: string | null;
  status: string;
  verticals: string[];
  states: string[];
  max_daily_volume: number | null;
  current_daily_count: number;
  webhook_url: string | null;
  ringba_url: string | null;
  retreaver_url: string | null;
  redirect_url: string | null;
  postback_url: string | null;
  s2s_url: string | null;
  ping_url: string | null;
  ping_enabled: boolean;
  payout_type: string;
  fixed_payout: number | null;
  min_bid: number | null;
  max_bid: number | null;
  acceptance_rate: number;
  min_age: number | null;
  max_age: number | null;
  require_homeowner: boolean | null;
  min_credit: string | null;
  schedule_enabled: boolean;
  schedule_start_hour: number;
  schedule_end_hour: number;
  schedule_timezone: string;
  response_format: string;
  custom_headers: Record<string, string>;
  field_mapping: Record<string, string>;
  notes: string | null;
  created_at: string;
}

const VERTICALS = [
  'auto_insurance', 'home_insurance', 'health_insurance', 'life_insurance',
  'debt_relief', 'debt_consolidation', 'personal_loan', 'mortgage_refi',
  'solar', 'medicare', 'tax_relief', 'home_security',
];

interface Offer {
  id: string;
  name: string;
  vertical_id: string;
  payout: number;
  payout_type: string;
  route_type: string;
  status: string;
  daily_cap: number | null;
  current_daily_count: number;
  total_leads: number;
  total_revenue: number;
  created_at: string;
}

type EditTab = 'general' | 'endpoints' | 'targeting' | 'schedule' | 'advanced' | 'offers';

export default function PortalBuyers() {
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Buyer>>({});
  const [editTab, setEditTab] = useState<EditTab>('general');
  const [saving, setSaving] = useState(false);

  const [buyerOffers, setBuyerOffers] = useState<Offer[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);

  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBuyers((await supabaseGet('buyers?order=created_at.desc')) || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!formName || !formEmail) return;
    setCreating(true);
    try {
      await supabasePost('buyers', { name: formName, contact_email: formEmail, status: 'active', verticals: ['auto_insurance'], max_daily_volume: 100 });
      setShowCreate(false); setFormName(''); setFormEmail(''); load();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const startEdit = async (b: Buyer) => {
    setEditingId(b.id); setEditForm({ ...b }); setEditTab('general');
    setOffersLoading(true);
    try {
      setBuyerOffers((await supabaseGet(`offers?buyer_id=eq.${b.id}&order=created_at.desc`)) || []);
    } catch { setBuyerOffers([]); }
    finally { setOffersLoading(false); }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const { id, created_at, current_daily_count, acceptance_rate, ...updates } = editForm as any;
      await supabasePatch(`buyers?id=eq.${editingId}`, updates);
      setBuyers(prev => prev.map(b => b.id === editingId ? { ...b, ...updates } : b));
      setEditingId(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const toggle = async (b: Buyer) => {
    const s = b.status === 'active' ? 'paused' : 'active';
    await supabasePatch(`buyers?id=eq.${b.id}`, { status: s }).catch(() => {});
    setBuyers(prev => prev.map(x => x.id === b.id ? { ...x, status: s } : x));
  };

  const u = (k: string, v: any) => setEditForm(f => ({ ...f, [k]: v }));

  // ── Full editor ──
  if (editingId) {
    return (
      <PortalLayout>
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save
            </Button>
          </div>
          <h1 className="text-xl font-bold text-white">{editForm.name}</h1>

          <div className="flex gap-1 flex-wrap border-b border-slate-700 pb-2">
            {([['general', 'General', Store], ['offers', `Offers (${buyerOffers.length})`, Tag], ['endpoints', 'Endpoints', Globe], ['targeting', 'Targeting', MapPin], ['schedule', 'Schedule', Clock], ['advanced', 'Advanced', Settings]] as [EditTab, string, any][]).map(([k, l, I]) => (
              <button key={k} onClick={() => setEditTab(k)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium ${editTab === k ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'}`}>
                <I className="h-3.5 w-3.5" /> {l}
              </button>
            ))}
          </div>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5 space-y-4">
              {editTab === 'general' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Advertiser Name" value={editForm.name} onChange={v => u('name', v)} />
                    <F label="Company" value={editForm.company} onChange={v => u('company', v)} />
                    <F label="Email" value={editForm.contact_email} onChange={v => u('contact_email', v)} />
                    <F label="Phone" value={editForm.phone} onChange={v => u('phone', v)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Daily Cap" value={editForm.max_daily_volume} onChange={v => u('max_daily_volume', parseInt(v) || null)} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Verticals (select multiple)</label>
                    <div className="flex flex-wrap gap-1.5">
                      {VERTICALS.map(v => (
                        <button key={v} onClick={() => {
                          const cur = editForm.verticals || [];
                          u('verticals', cur.includes(v) ? cur.filter((x: string) => x !== v) : [...cur, v]);
                        }} className={`px-2 py-1 rounded text-xs ${(editForm.verticals || []).includes(v) ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
                          {v.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Notes</label>
                    <textarea value={editForm.notes || ''} onChange={e => u('notes', e.target.value)} rows={3} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                </div>
              )}

              {editTab === 'endpoints' && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-400">Configure where leads and call data get sent.</p>
                  <F label="Webhook URL (POST JSON)" value={editForm.webhook_url} onChange={v => u('webhook_url', v)} placeholder="https://api.buyer.com/leads" />
                  <F label="Ringba URL" value={editForm.ringba_url} onChange={v => u('ringba_url', v)} placeholder="https://display.ringba.com/enrich/v1/pub/..." />
                  <F label="Retreaver URL" value={editForm.retreaver_url} onChange={v => u('retreaver_url', v)} placeholder="https://api.retreaver.com/..." />
                  <F label="Redirect URL" value={editForm.redirect_url} onChange={v => u('redirect_url', v)} placeholder="https://buyer.com/offer?lead_id={id}" />
                  <div className="border-t border-slate-700 pt-4">
                    <h4 className="text-sm font-medium text-white mb-3">Server-to-Server (S2S) Tracking</h4>
                    <F label="Postback URL (fired on conversion)" value={editForm.postback_url} onChange={v => u('postback_url', v)} placeholder="https://tracking.buyer.com/postback?click_id={click_id}&payout={payout}" />
                    <div className="mt-2 text-[10px] text-slate-500">Available macros: {'{session_id}'}, {'{click_id}'}, {'{payout}'}, {'{vertical}'}, {'{sub_id}'}, {'{affiliate_id}'}</div>
                    <F label="S2S Conversion URL" value={editForm.s2s_url} onChange={v => u('s2s_url', v)} placeholder="https://network.com/s2s?txn_id={session_id}&amount={payout}" />
                  </div>
                  <div className="border-t border-slate-700 pt-4">
                    <h4 className="text-sm font-medium text-white mb-3">Ping/Post</h4>
                    <label className="flex items-center gap-2 text-sm text-slate-300 mb-2">
                      <input type="checkbox" checked={editForm.ping_enabled || false} onChange={e => u('ping_enabled', e.target.checked)} className="rounded border-slate-600" />
                      Enable ping before post (check acceptance before sending full lead)
                    </label>
                    {editForm.ping_enabled && (
                      <F label="Ping URL" value={editForm.ping_url} onChange={v => u('ping_url', v)} placeholder="https://api.buyer.com/ping" />
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Response Format</label>
                    <select value={editForm.response_format || 'json'} onChange={e => u('response_format', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="json">JSON</option>
                      <option value="xml">XML</option>
                      <option value="form">Form Encoded</option>
                    </select>
                  </div>
                </div>
              )}

              {editTab === 'targeting' && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-400">Filter which leads this buyer accepts based on user profile.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Min Age" value={editForm.min_age} onChange={v => u('min_age', parseInt(v) || null)} placeholder="18" />
                    <F label="Max Age" value={editForm.max_age} onChange={v => u('max_age', parseInt(v) || null)} placeholder="65" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Minimum Credit Tier</label>
                    <select value={editForm.min_credit || ''} onChange={e => u('min_credit', e.target.value || null)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">No minimum</option>
                      <option value="poor">Poor (any)</option>
                      <option value="fair">Fair+</option>
                      <option value="good">Good+</option>
                      <option value="excellent">Excellent only</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={editForm.require_homeowner || false} onChange={e => u('require_homeowner', e.target.checked)} className="rounded border-slate-600" />
                    Require homeowner
                  </label>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Accepted States (empty = all)</label>
                    <input value={(editForm.states || []).join(', ')} onChange={e => u('states', e.target.value.split(',').map((s: string) => s.trim().toUpperCase()).filter(Boolean))} placeholder="CA, TX, FL, NY" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                    <div className="text-[10px] text-slate-500 mt-1">Comma-separated state codes</div>
                  </div>
                </div>
              )}

              {editTab === 'schedule' && (
                <div className="space-y-4">
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={editForm.schedule_enabled || false} onChange={e => u('schedule_enabled', e.target.checked)} className="rounded border-slate-600" />
                    Only accept leads during business hours
                  </label>
                  {editForm.schedule_enabled && (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Start Hour</label>
                        <select value={editForm.schedule_start_hour || 8} onChange={e => u('schedule_start_hour', parseInt(e.target.value))} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                          {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">End Hour</label>
                        <select value={editForm.schedule_end_hour || 20} onChange={e => u('schedule_end_hour', parseInt(e.target.value))} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                          {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Timezone</label>
                        <select value={editForm.schedule_timezone || 'America/New_York'} onChange={e => u('schedule_timezone', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                          <option value="America/New_York">Eastern</option>
                          <option value="America/Chicago">Central</option>
                          <option value="America/Denver">Mountain</option>
                          <option value="America/Los_Angeles">Pacific</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {editTab === 'advanced' && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Custom Headers (JSON)</label>
                    <textarea value={JSON.stringify(editForm.custom_headers || {}, null, 2)} onChange={e => { try { u('custom_headers', JSON.parse(e.target.value)); } catch {} }} rows={4} placeholder='{"X-API-Key": "abc123"}' className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-emerald-400 text-xs font-mono" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Field Mapping (JSON) — map IntentDrive fields to buyer's expected field names</label>
                    <textarea value={JSON.stringify(editForm.field_mapping || {}, null, 2)} onChange={e => { try { u('field_mapping', JSON.parse(e.target.value)); } catch {} }} rows={6} placeholder='{"email": "lead_email", "phone": "phone_number", "full_name": "contact_name"}' className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-emerald-400 text-xs font-mono" />
                  </div>
                </div>
              )}

              {editTab === 'offers' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-400">All offers linked to this advertiser. Payouts and caps are configured per offer.</p>
                    <Link to="/portal/offers">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                        <Plus className="h-3.5 w-3.5 mr-1" /> Create Offer
                      </Button>
                    </Link>
                  </div>

                  {offersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 text-emerald-400 animate-spin" />
                    </div>
                  ) : buyerOffers.length === 0 ? (
                    <div className="text-center py-8">
                      <Tag className="h-8 w-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">No offers yet for this advertiser.</p>
                      <p className="text-xs text-slate-500 mt-1">Go to Offers to create one and assign it to this advertiser.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {buyerOffers.map(o => (
                        <div key={o.id} className={`flex items-center gap-3 p-3 rounded-lg border ${o.status === 'active' ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-60'}`}>
                          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
                            <DollarSign className="h-4 w-4 text-amber-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-white">{o.name}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${o.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'}`}>{o.status}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                              <span className="text-amber-400 font-medium">${o.payout}/{o.payout_type?.replace('_', ' ')}</span>
                              <span>{o.vertical_id?.replace(/_/g, ' ')}</span>
                              <span>{o.route_type}</span>
                              {o.daily_cap && <span>{o.current_daily_count || 0}/{o.daily_cap}/day</span>}
                              {o.total_leads > 0 && <span>{o.total_leads} leads</span>}
                              {o.total_revenue > 0 && <span className="text-emerald-400">${o.total_revenue.toFixed(2)} rev</span>}
                            </div>
                          </div>
                          <Link to="/portal/offers" className="flex items-center gap-1 px-2 py-1 rounded bg-slate-700/50 text-xs text-slate-300 hover:bg-slate-700">
                            <Pencil className="h-3 w-3" /> Edit
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  // ── List view ──
  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Store className="h-6 w-6 text-emerald-400" /> Advertisers
            </h1>
            <p className="text-sm text-slate-400 mt-1">Manage advertisers with endpoints, targeting, and scheduling.</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-1" /> Add Advertiser
          </Button>
        </div>

        {/* Global Postback URL */}
        <Card className="bg-emerald-500/5 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white">Global Postback URL</span>
              <span className="text-[10px] text-slate-500">Give this to every advertiser</span>
            </div>
            <div className="bg-slate-900 rounded-lg p-3 font-mono text-xs text-emerald-400 break-all select-all">
              https://intentdrive.ai/api/postback?session_id={'{session_id}'}&payout={'{payout}'}&status=converted&txn_id={'{txn_id}'}&offer_id={'{offer_id}'}&sub_id={'{sub_id}'}
            </div>
            <div className="mt-2 text-[10px] text-slate-500">
              <strong>Everflow:</strong> Use {'{transaction_id}'} for session_id, {'{payout}'} for payout, {'{conversion_id}'} for txn_id, {'{sub1}'}-{'{sub5}'} for sub tracking
            </div>
            <div className="mt-1 text-[10px] text-slate-500">
              <strong>Supported params:</strong> session_id, payout/revenue/amount, status (converted/rejected/returned), txn_id, offer_id, sub_id, vertical, buyer_id
            </div>
          </CardContent>
        </Card>

        {showCreate && (
          <Card className="bg-slate-800/50 border-emerald-500/30">
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <F label="Advertiser Name *" value={formName} onChange={setFormName} placeholder="InsureCo Direct" />
                <F label="Email *" value={formEmail} onChange={setFormEmail} placeholder="buyer@company.com" />
              </div>
              <div className="flex gap-2">
                <Button onClick={create} disabled={creating || !formName || !formEmail} className="bg-emerald-600 hover:bg-emerald-700">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : buyers.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8 text-center">
              <Store className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No advertisers yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {buyers.map(b => (
              <Card key={b.id} className={`border ${b.status === 'active' ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-60'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                      <DollarSign className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white">{b.name}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${b.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'}`}>{b.status}</span>
                        {b.max_daily_volume && <span className="text-[10px] text-slate-500">{b.current_daily_count}/{b.max_daily_volume}/day</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{b.contact_email}</span>
                        {b.webhook_url && <span className="text-blue-400">Webhook</span>}
                        {b.ringba_url && <span className="text-emerald-400">Ringba</span>}
                        {b.postback_url && <span className="text-violet-400">Postback</span>}
                        {b.s2s_url && <span className="text-amber-400">S2S</span>}
                        {b.ping_enabled && <span className="text-cyan-400">Ping</span>}
                        {b.schedule_enabled && <span className="text-pink-400">Scheduled</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => startEdit(b)} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600/20 text-xs text-emerald-400 hover:bg-emerald-600/30">
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                      <Button size="sm" variant="outline" onClick={() => toggle(b)}>
                        {b.status === 'active' ? <><Pause className="h-3 w-3 mr-1" /> Pause</> : <><Play className="h-3 w-3 mr-1" /> Activate</>}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}

function F({ label, value, onChange, placeholder }: { label: string; value: any; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
    </div>
  );
}
